import _ from "lodash";

import { matchPath } from "react-router-dom";
import type { Location } from "history";

import QueryString from "core/QueryString";

import routesMapped from "../routesMapped";
import type { RouteItem } from "../routesObject";

/**
 * Extended route type that includes location info and matched params
 */
export interface ActiveRouteType extends Omit<RouteItem, "component"> {
  pathname: string;
  search: string;
  hash: string;
  query: Record<string, string | string[]>;
  params: Record<string, string>;
  isExact: boolean;
  path: string;
  state?: unknown;
}

/**
 * Fallback route for when no match is found (404)
 */
function createFallbackRoute(
  location: Location | typeof window.location
): ActiveRouteType {
  const search = _.isString(location.search) ? location.search : "";

  return {
    pathname: location.pathname,
    search,
    hash: location.hash || "",
    key: "error",
    url: "*",
    title: "404",
    authTypes: [],
    authTypesForbidden: [],
    hideFooterOnMobile: false,
    hideHeader: false,
    hideFooter: false,
    slug: "error",
    query: QueryString.parse(search),
    params: {},
    isExact: false,
    path: "",
    state: (location as Location).state
  };
}

/**
 * Matches a location (pathname) to a route from routesObject
 * and returns the full route item with params, query, etc.
 *
 * @param location - Either a history Location object or window.location
 * @returns The matched route item with all metadata
 */
export function matchRouteItem(
  location: Location | typeof window.location
): ActiveRouteType {
  const routes = routesMapped.returnRoutes();
  const search = _.isString(location.search) ? location.search : "";

  for (const route of routes) {
    const match = matchPath(location.pathname, {
      path: route.url,
      exact: true,
      strict: false
    });

    if (match) {
      return {
        pathname: location.pathname,
        search,
        hash: location.hash || "",
        ..._.omit(route, "component"),
        query: QueryString.parse(search),
        params: (match.params ?? {}) as Record<string, string>,
        isExact: match.isExact,
        path: match.path,
        state: (location as Location).state
      } as ActiveRouteType;
    }
  }

  // Fallback - should never reach here due to error "*" catch-all in routesObject
  return createFallbackRoute(location);
}

/**
 * Matches the current browser location to a route
 * Useful in sagas or non-React code where you don't have access to useLocation
 *
 * @param historyLocation - The history.location object
 * @returns The matched route item with all metadata
 */
export function getCurrentRoute(historyLocation: Location): ActiveRouteType {
  return matchRouteItem(historyLocation);
}
